import { CSSObjectWithLabel, StylesConfig } from 'react-select'

import theme from '../../theme'
import { ISelectOption, SelectValue } from './types'

/**
 * Altura do select, no mesmo par de tokens que o resto do painel: `md` é a altura de campo de
 * formulário e `sm` é a da barra de filtros (o `FILTER_CONTROL_SIZE`). Não existe um terceiro.
 */
export type SelectSize = 'sm' | 'md'

const { colors, fontSizes, radii, sizes, space, zIndices } = theme

/**
 * `popover` (1500) fica acima do `modal` (1400) do Chakra, que é o que faz o menu aparecer por
 * cima do modal em vez de sumir atrás dele. Era o `zIndex: 9999` copiado em 19 lugares — e em 11
 * deles copiado no alvo errado (`menu`, não `menuPortal`), o que não resolve nada sem portal.
 */
export const SELECT_MENU_Z_INDEX = zIndices.popover

/**
 * Geometria por tamanho, tirada do tema do `<Input>` do Chakra — é o que faz o select ficar
 * alinhado com o campo de texto ao lado dele. Antes eram três alturas concorrentes na mesma tela:
 * react-select 38px, `<Select>` do Chakra 40px e o `SearchableSelect` antigo 32px.
 */
const SIZES: Record<SelectSize, { minHeight: string; fontSize: string; paddingX: string; borderRadius: string }> = {
  md: { minHeight: sizes[10], fontSize: fontSizes.md, paddingX: space[4], borderRadius: radii.md },
  sm: { minHeight: sizes[8], fontSize: fontSizes.sm, paddingX: space[3], borderRadius: radii.sm }
}

/**
 * A aparência única do react-select no painel, montada a partir dos tokens do tema.
 *
 * Havia quatro `selectStyles` locais (três deles quase idênticos, com raio e altura divergentes) e
 * 30 selects sem estilo nenhum, ou seja, com a aparência de fábrica da biblioteca. Nenhuma página
 * declara mais borda, raio, cor ou sombra: o que a página declara é tamanho e estado.
 *
 * O anel de foco é o `blue.500` do Chakra de propósito — é o que todos os `<Input>` do painel usam,
 * e um select que foca em magenta ao lado de um input que foca em azul deixa o formulário com duas
 * cores de foco. Trocar a cor de foco do painel inteiro é decisão de tema, não de select.
 */
export function buildSelectStyles<T extends SelectValue, IsMulti extends boolean>(
  size: SelectSize,
  isInvalid: boolean
): StylesConfig<ISelectOption<T>, IsMulti> {
  const { minHeight, fontSize, paddingX, borderRadius } = SIZES[size]

  return {
    control: (base: CSSObjectWithLabel, state) => ({
      ...base,
      minHeight,
      fontSize,
      borderRadius,
      backgroundColor: colors.white,
      borderColor: state.isFocused ? colors.blue[500] : isInvalid ? colors.red[500] : colors.gray[200],
      boxShadow: state.isFocused
        ? `0 0 0 1px ${colors.blue[500]}`
        : isInvalid
          ? `0 0 0 1px ${colors.red[500]}`
          : 'none',
      // O Chakra marca campo desabilitado com opacidade, não com fundo cinza (o padrão da lib).
      opacity: state.isDisabled ? 0.4 : 1,
      cursor: state.isDisabled ? 'not-allowed' : 'default',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      '&:hover': {
        borderColor: state.isFocused ? colors.blue[500] : isInvalid ? colors.red[500] : colors.gray[300]
      }
    }),
    valueContainer: (base: CSSObjectWithLabel) => ({ ...base, padding: `2px ${paddingX}`, gap: '4px' }),
    input: (base: CSSObjectWithLabel) => ({ ...base, margin: 0, padding: 0, color: colors.gray[800] }),
    placeholder: (base: CSSObjectWithLabel) => ({ ...base, margin: 0, color: colors.gray[500] }),
    singleValue: (base: CSSObjectWithLabel) => ({ ...base, margin: 0, color: colors.gray[800] }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base: CSSObjectWithLabel) => ({
      ...base,
      padding: '0 8px',
      color: colors.gray[500],
      '&:hover': { color: colors.gray[600] }
    }),
    clearIndicator: (base: CSSObjectWithLabel) => ({
      ...base,
      padding: '0 4px',
      color: colors.gray[500],
      '&:hover': { color: colors.gray[700] }
    }),
    loadingIndicator: (base: CSSObjectWithLabel) => ({ ...base, color: colors.gray[500] }),
    menu: (base: CSSObjectWithLabel) => ({
      ...base,
      marginTop: '4px',
      borderRadius: radii.md,
      border: `1px solid ${colors.gray[200]}`,
      boxShadow: theme.shadows.lg,
      overflow: 'hidden',
      zIndex: SELECT_MENU_Z_INDEX
    }),
    menuPortal: (base: CSSObjectWithLabel) => ({ ...base, zIndex: SELECT_MENU_Z_INDEX }),
    menuList: (base: CSSObjectWithLabel) => ({ ...base, padding: '4px 0' }),
    option: (base: CSSObjectWithLabel, state) => ({
      ...base,
      fontSize,
      padding: '8px 12px',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      color: state.isSelected ? colors.white : state.isDisabled ? colors.gray[400] : colors.gray[800],
      backgroundColor: state.isSelected
        ? colors.magenta[500]
        : state.isFocused && !state.isDisabled
          ? colors.gray[100]
          : 'transparent',
      '&:active': { backgroundColor: state.isSelected ? colors.magenta[500] : colors.gray[200] }
    }),
    noOptionsMessage: (base: CSSObjectWithLabel) => ({ ...base, fontSize, color: colors.gray[500] }),
    loadingMessage: (base: CSSObjectWithLabel) => ({ ...base, fontSize, color: colors.gray[500] }),
    multiValue: (base: CSSObjectWithLabel) => ({
      ...base,
      margin: 0,
      borderRadius: radii.base,
      backgroundColor: colors.gray[100]
    }),
    multiValueLabel: (base: CSSObjectWithLabel) => ({ ...base, fontSize: fontSizes.sm, color: colors.gray[800] }),
    multiValueRemove: (base: CSSObjectWithLabel) => ({
      ...base,
      color: colors.gray[500],
      '&:hover': { backgroundColor: colors.gray[200], color: colors.gray[700] }
    })
  }
}
